import { useState } from 'react';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import { MODAL_SIZES, MODAL_VARIANTS, Modal, ModalBody, ModalFooter, ModalHeader } from 'ui/modal';
import { colors, fontSizes, spacing, smallShadow } from 'ui/theme';
import { ActionType, CONTROL_BAR_OPTIONS } from './constants';
import { NAVIGATION_TIPS_MODAL } from '../../../constants';
import { closeModal } from '../../../ducks';

type Props = {
  /** Whether modal is open and visible */
  isOpen: boolean;
};

const StyledDiv = styled.div`
  display: flex;
  flex-wrap: wrap;
  padding: 0 20px;
  line-height: 50px;
  font-size: ${fontSizes.s14};
  :nth-of-type(odd) {
    background: ${colors.grey1};
  }
  > span:nth-of-type(1) {
    flex: auto;
  }
  kbd {
    border: 1px solid ${colors.grey50};
    color: ${colors.textPrimary};
    margin: 0 5px;
    padding: 5px;
    background: inherit;
    font: inherit;
  }
`;

const ShortcutKey = styled.span`
  color: ${colors.grey50};
  > :last-child {
    margin-right: 0;
  }
`;

const AdditionalShortcut = styled(ShortcutKey)`
  width: 100%;
  text-align: right;
`;

const ControlBar = styled.div`
  margin-bottom: ${spacing.half};
  display: flex;
  border: 1px solid ${colors.grey15};
  border-radius: ${spacing.quarter};
  padding: ${spacing.quarter} 0;
  font-size: ${fontSizes.s14};
  background: ${colors.grey5};
`;

const NavType = styled.button<{ isSelected: boolean }>`
  flex: 1;
  border: 0;
  margin: 0 ${spacing.quarter};
  padding: ${spacing.quarter};
  border-radius: ${spacing.quarter};
  background: inherit;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${colors.textSecondary};

  ${({ isSelected }) =>
    isSelected &&
    `
    background: ${colors.white};
    color: ${colors.textPrimary};
    ${smallShadow};
  `};

  svg {
    margin-left: ${spacing.quarter};
  }
`;

const NavigationTipRow = ({ action, shortcuts: [sc1, sc2] }: ActionType) => (
  <StyledDiv>
    <span>{action}</span>
    <ShortcutKey>{sc1}</ShortcutKey>
    {sc2 && <AdditionalShortcut>{sc2}</AdditionalShortcut>}
  </StyledDiv>
);

export const NavigationTipsModal = ({ isOpen }: Props) => {
  const dispatch = useDispatch();

  const [navigationType, setNavigationType] = useState(CONTROL_BAR_OPTIONS[0]);

  const closeNavigationTips = () => dispatch(closeModal(NAVIGATION_TIPS_MODAL));
  return (
    <Modal
      id={NAVIGATION_TIPS_MODAL}
      isModalOpen={isOpen}
      onClose={closeNavigationTips}
      size={MODAL_SIZES.MEDIUM}
      variant={MODAL_VARIANTS.DEFAULT}
    >
      <ModalHeader>Navigation tips</ModalHeader>
      <ModalBody>
        <ControlBar data-test={`${navigationType.name}-selected`}>
          {CONTROL_BAR_OPTIONS.map((item) => {
            const { name, icon: Icon } = item;
            return (
              <NavType
                key={name}
                data-test={`${name}-option`}
                onClick={() => setNavigationType(item)}
                isSelected={navigationType.name === name}
              >
                {name} <Icon isColorInherited={true} />
              </NavType>
            );
          })}
        </ControlBar>

        {navigationType.tips.map(({ action, shortcuts }: ActionType) => (
          <NavigationTipRow key={action} action={action} shortcuts={shortcuts} />
        ))}
      </ModalBody>
      <ModalFooter hasCancel={false}></ModalFooter>
    </Modal>
  );
};
